import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiUpload, FiFileText, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { PATHS } from './utils/platformPaths';

const AlumniProfileManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Resume Upload State
  const [resumeFile, setResumeFile] = useState(null);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  
  // LinkedIn Import State
  const [linkedinInput, setLinkedinInput] = useState('');
  const [isPreviewingLinkedin, setIsPreviewingLinkedin] = useState(false);
  const [isImportingLinkedin, setIsImportingLinkedin] = useState(false);
  const [linkedinPreview, setLinkedinPreview] = useState(null);
  const [showLinkedinModal, setShowLinkedinModal] = useState(false);
  const [linkedinProgress, setLinkedinProgress] = useState({
    step: '',
    message: '',
    showProgress: false
  });

  useEffect(() => {
    fetchProfile();
    // Check if we should scroll to a specific section
    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/api/alumni/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeUpload = async (e) => {
    e.preventDefault();
    if (!resumeFile) return;

    setIsUploadingResume(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const formData = new FormData();
      formData.append('file', resumeFile);

      const response = await fetch(`${backendUrl}/api/alumni/upload-resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const data = await response.json();
      setProfile(prev => ({ ...prev, resume_url: data.resume_url }));
      setSuccessMessage('Resume uploaded successfully!');
      setResumeFile(null);
      
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Resume upload error:', error);
      setError(error.message || 'Failed to upload resume');
    } finally {
      setIsUploadingResume(false);
    }
  };

  const handleLinkedinPreview = async () => {
    if (!linkedinInput.trim()) {
      setError('Please enter a LinkedIn URL or username');
      return;
    }

    setIsPreviewingLinkedin(true);
    setLinkedinProgress({
      step: 1,
      message: 'Initializing LinkedIn scraper...',
      showProgress: true
    });
    setError('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      // Update progress
      setLinkedinProgress({
        step: 2,
        message: 'Connecting to import your Profile...',
        showProgress: true
      });

      const response = await fetch(`${backendUrl}/api/alumni/linkedin/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ linkedin_input: linkedinInput })
      });

      // Update progress
      setLinkedinProgress({
        step: 3,
        message: 'Scraping LinkedIn profile data...',
        showProgress: true
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to preview LinkedIn profile');
      }

      // Update progress
      setLinkedinProgress({
        step: 4,
        message: 'Processing profile information...',
        showProgress: true
      });

      const data = await response.json();
      setLinkedinPreview(data.preview);
      setShowLinkedinModal(true);
      setSuccessMessage('LinkedIn profile preview loaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('LinkedIn preview error:', error);
      setError(error.message || 'Failed to preview LinkedIn data');
    } finally {
      setIsPreviewingLinkedin(false);
      setLinkedinProgress({ step: '', message: '', showProgress: false });
    }
  };

  const handleLinkedinImport = async () => {
    if (!linkedinPreview) return;

    setIsImportingLinkedin(true);
    setLinkedinProgress({
      step: 1,
      message: 'Re-fetching LinkedIn data...',
      showProgress: true
    });
    setError('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      // Update progress
      setLinkedinProgress({
        step: 2,
        message: 'Mapping data to profile fields...',
        showProgress: true
      });
      
      const response = await fetch(`${backendUrl}/api/alumni/linkedin/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          linkedin_input: linkedinInput
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to import LinkedIn profile');
      }

      // Update progress
      setLinkedinProgress({
        step: 3,
        message: 'Updating your profile...',
        showProgress: true
      });

      const data = await response.json();
      
      // Update progress
      setLinkedinProgress({
        step: 4,
        message: 'Refreshing dashboard...',
        showProgress: true
      });

      setSuccessMessage(`Successfully imported ${data.imported_fields?.length || 0} fields from LinkedIn!`);
      setLinkedinInput('');
      setLinkedinPreview(null);
      setShowLinkedinModal(false);
      // Refresh profile to show updated data
      await fetchProfile();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('LinkedIn import error:', error);
      setError(error.message || 'Failed to import LinkedIn data');
    } finally {
      setIsImportingLinkedin(false);
      setLinkedinProgress({ step: '', message: '', showProgress: false });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate(PATHS.dashboard)}
                className="mr-4 p-2 rounded-md hover:bg-gray-100 transition-colors"
              >
                <FiArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Profile Management</h1>
                <p className="text-sm text-gray-600">Manage your resume and LinkedIn profile</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <FiCheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <FiAlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Resume Upload Section */}
        <div id="resume-section" className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Resume Management
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Upload your resume to apply for job opportunities
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            {profile?.resume_url && (
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center">
                  <FiCheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-sm font-medium text-green-800">
                    Resume uploaded successfully
                  </span>
                </div>
                <p className="mt-1 text-sm text-green-700">
                  Your resume is ready for job applications
                </p>
              </div>
            )}

            <form onSubmit={handleResumeUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Resume (PDF, DOC, DOCX)
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setResumeFile(e.target.files[0])}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-medium
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Maximum file size: 5MB
                </p>
              </div>

              <button
                type="submit"
                disabled={!resumeFile || isUploadingResume}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  !resumeFile || isUploadingResume
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                <FiUpload className="mr-2 h-4 w-4" />
                {isUploadingResume ? 'Uploading...' : profile?.resume_url ? 'Update Resume' : 'Upload Resume'}
              </button>
            </form>
          </div>
        </div>

        {/* LinkedIn Import Section */}
        <div id="linkedin-section" className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              LinkedIn Profile Import
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Import or re-import your professional information from LinkedIn
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            {profile?.skills_list && profile.skills_list.length > 0 && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center">
                  <FiCheckCircle className="h-5 w-5 text-blue-500 mr-2" />
                  <span className="text-sm font-medium text-blue-800">
                    LinkedIn profile imported
                  </span>
                </div>
                <p className="mt-1 text-sm text-blue-700">
                  Your LinkedIn data has been successfully imported. You can re-import anytime to update your profile with the latest LinkedIn information.
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn Profile URL or Username
                </label>
                <input
                  type="text"
                  value={linkedinInput}
                  onChange={(e) => setLinkedinInput(e.target.value)}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Enter your LinkedIn profile URL or username..."
                />
                <p className="mt-2 text-sm text-gray-500">
                  {profile?.skills_list && profile.skills_list.length > 0 
                    ? 'Enter your LinkedIn URL or username to update your profile with the latest information'
                    : 'Enter your LinkedIn profile URL or username to import your professional data'
                  }
                </p>
              </div>

              <button
                onClick={handleLinkedinPreview}
                disabled={!linkedinInput || isPreviewingLinkedin}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  !linkedinInput || isPreviewingLinkedin
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                <FiFileText className="mr-2 h-4 w-4" />
                {isPreviewingLinkedin 
                  ? 'Processing...' 
                  : profile?.skills_list && profile.skills_list.length > 0 
                    ? 'Preview LinkedIn Update' 
                    : 'Preview Import'
                }
              </button>
            </div>

            {/* Progress Indicator */}
            {linkedinProgress.showProgress && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center mb-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-blue-900">
                        Step {linkedinProgress.step} of 4
                      </p>
                      <p className="text-xs text-blue-600">
                        {Math.round((linkedinProgress.step / 4) * 100)}%
                      </p>
                    </div>
                    <p className="text-sm text-blue-700">{linkedinProgress.message}</p>
                  </div>
                </div>
                {/* Progress Bar */}
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${(linkedinProgress.step / 4) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* LinkedIn Preview Modal */}
      {showLinkedinModal && linkedinPreview && (
        <LinkedInPreviewModal
          preview={linkedinPreview}
          onConfirm={handleLinkedinImport}
          onCancel={() => {
            setShowLinkedinModal(false);
            setLinkedinPreview(null);
          }}
          isImporting={isImportingLinkedin}
        />
      )}
    </div>
  );
};

// LinkedIn Preview Modal Component
const LinkedInPreviewModal = ({ preview, onConfirm, onCancel, isImporting }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">LinkedIn Import Preview</h3>
          <p className="text-sm text-gray-500">Review the data before importing</p>
        </div>
        
        <div className="px-6 py-4 space-y-6">
          {/* Basic Info */}
          {preview.basic_info && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Basic Information</h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex">
                  <span className="text-sm font-medium text-gray-600 w-32">Name:</span>
                  <span className="text-sm text-gray-900">
                    {preview.basic_info['First Name']} {preview.basic_info['Last Name']}
                  </span>
                </div>
                {preview.basic_info['Location'] && (
                  <div className="flex">
                    <span className="text-sm font-medium text-gray-600 w-32">Location:</span>
                    <span className="text-sm text-gray-900">{preview.basic_info['Location']}</span>
                  </div>
                )}
                {preview.basic_info['LinkedIn URL'] && (
                  <div className="flex">
                    <span className="text-sm font-medium text-gray-600 w-32">LinkedIn URL:</span>
                    <span className="text-sm text-gray-900 truncate">{preview.basic_info['LinkedIn URL']}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Current Position */}
          {preview.current_position && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Current Position</h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex">
                  <span className="text-sm font-medium text-gray-600 w-32">Employer:</span>
                  <span className="text-sm text-gray-900">{preview.current_position['Current Employer']}</span>
                </div>
                <div className="flex">
                  <span className="text-sm font-medium text-gray-600 w-32">Position:</span>
                  <span className="text-sm text-gray-900">{preview.current_position['Current Position']}</span>
                </div>
                <div className="flex">
                  <span className="text-sm font-medium text-gray-600 w-32">Experience:</span>
                  <span className="text-sm text-gray-900">{preview.current_position['Years of Experience']}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Education */}
          {preview.education && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Education</h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex">
                  <span className="text-sm font-medium text-gray-600 w-32">University:</span>
                  <span className="text-sm text-gray-900">{preview.education['University']}</span>
                </div>
                <div className="flex">
                  <span className="text-sm font-medium text-gray-600 w-32">Major:</span>
                  <span className="text-sm text-gray-900">{preview.education['Major']}</span>
                </div>
                <div className="flex">
                  <span className="text-sm font-medium text-gray-600 w-32">Degree:</span>
                  <span className="text-sm text-gray-900">{preview.education['Degree Type']}</span>
                </div>
                <div className="flex">
                  <span className="text-sm font-medium text-gray-600 w-32">Graduation:</span>
                  <span className="text-sm text-gray-900">{preview.education['Graduation Year']}</span>
                </div>
              </div>
            </div>
          )}

          {/* Summary */}
          {preview.summary && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Summary</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 whitespace-pre-line">{preview.summary}</p>
              </div>
            </div>
          )}

          {/* Skills */}
          {preview.skills && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Skills ({preview.skills.total_count || 0} total)
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {/* Endorsed Skills */}
                {preview.skills.top_endorsed && preview.skills.top_endorsed.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-600 mb-2">Top Endorsed ({preview.skills.endorsed_count || 0})</div>
                    <div className="flex flex-wrap gap-2">
                      {preview.skills.top_endorsed.map((skill, index) => (
                        <span key={`endorsed-${index}`} className="inline-block bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Sample Unendorsed Skills */}
                {preview.skills.sample_unendorsed && preview.skills.sample_unendorsed.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-600 mb-2">Sample Additional Skills</div>
                    <div className="flex flex-wrap gap-2">
                      {preview.skills.sample_unendorsed.map((skill, index) => (
                        <span key={`unendorsed-${index}`} className="inline-block bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Experience */}
          {preview.experience && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Work Experience
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex">
                  <span className="text-sm font-medium text-gray-600 w-32">Total Positions:</span>
                  <span className="text-sm text-gray-900">{preview.experience.total_positions || 0}</span>
                </div>
                {preview.experience.companies && preview.experience.companies.length > 0 && (
                  <div className="flex">
                    <span className="text-sm font-medium text-gray-600 w-32">Companies:</span>
                    <span className="text-sm text-gray-900">{preview.experience.companies.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Education History */}
          {preview.education_history && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Education History
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {preview.education_history.schools && preview.education_history.schools.map((school, index) => (
                  <div key={index} className="border-l-2 border-blue-400 pl-4">
                    <p className="text-sm font-medium text-gray-900">{school.school}</p>
                    <p className="text-sm text-gray-600">{school.degree}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            disabled={isImporting}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isImporting}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isImporting && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            )}
            {isImporting ? 'Importing Profile...' : 'Import Profile'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlumniProfileManagement;